import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { FileSearch, Upload, FileText, AlertCircle, CheckCircle2, Sparkles } from "lucide-react";
import { api } from "../api";
import { PageHeader, GlassCard, ScoreRing, Spinner, Disclaimer, Chip } from "../components/ui";

const BUREAUS = ["Experian", "Equifax", "TransUnion"];
const SEV = { high: "red", medium: "amber", low: "blue" };

export default function ReportAnalyzer() {
  const [mode, setMode] = useState("text");
  const [bureau, setBureau] = useState("Experian");
  const [text, setText] = useState("");
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [bureaus, setBureaus] = useState([]);
  const [bureauBusy, setBureauBusy] = useState(null);

  const loadHistory = () => api.get("/reports").then(({ data }) => setHistory(data));
  const loadBureaus = () => api.get("/bureaus").then(({ data }) => setBureaus(data));
  useEffect(() => { loadHistory(); loadBureaus(); }, []);

  const connectBureau = async (b) => {
    setBureauBusy(b);
    try { await api.post(`/bureaus/${b}/connect`); toast.success(`${b} connected (demo)`); loadBureaus(); }
    catch { toast.error("Connection failed"); } finally { setBureauBusy(null); }
  };
  const importBureau = async (b) => {
    setBureauBusy(b); setResult(null);
    try {
      const { data } = await api.post(`/bureaus/${b}/import`);
      setResult(data); loadHistory(); toast.success(`Imported & analyzed ${b} report`);
    } catch (e) { toast.error(e.response?.data?.detail || "Import failed"); }
    finally { setBureauBusy(null); }
  };

  const analyze = async () => {
    setLoading(true); setResult(null);
    try {
      let data;
      if (mode === "text") {
        if (text.trim().length < 40) { setLoading(false); return toast.error("Please paste more report text."); }
        ({ data } = await api.post("/reports/analyze", { text, bureau }));
      } else {
        if (!file) { setLoading(false); return toast.error("Please choose a PDF or CSV file."); }
        const fd = new FormData();
        fd.append("file", file); fd.append("bureau", bureau);
        ({ data } = await api.post("/reports/analyze-file", fd, { headers: { "Content-Type": "multipart/form-data" } }));
      }
      setResult(data);
      loadHistory();
      toast.success("Analysis complete");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Analysis failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="space-y-6">
      <PageHeader label="AI Tool" title="AI Credit Report Analyzer"
        subtitle="Upload or paste a report from Experian, Equifax, or TransUnion for a plain-English analysis." />

      <GlassCard className="p-6">
        <div className="flex flex-wrap items-center gap-2 mb-4">
          {["text", "file"].map((m) => (
            <button key={m} onClick={() => setMode(m)} data-testid={`analyzer-mode-${m}`}
              className={`rounded-full px-4 py-2 text-sm ${mode === m ? "cc-gradient text-white" : "border border-white/10 text-slate-400 cc-nav-item"}`}>
              {m === "text" ? "Paste Text" : "Upload File (PDF/CSV)"}
            </button>
          ))}
          <div className="ml-auto flex items-center gap-2">
            <span className="text-xs text-slate-500">Bureau</span>
            <select value={bureau} onChange={(e) => setBureau(e.target.value)} className="cc-input rounded-lg px-3 py-2 text-sm" data-testid="analyzer-bureau">
              {BUREAUS.map((b) => <option key={b} className="bg-[#1a1a24]">{b}</option>)}
            </select>
          </div>
        </div>

        {mode === "text" ? (
          <textarea value={text} onChange={(e) => setText(e.target.value)} rows={7}
            placeholder="Paste the text of your credit report here…"
            className="cc-input w-full rounded-xl px-4 py-3 text-sm" data-testid="analyzer-text" />
        ) : (
          <label className="flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-white/10 cc-surface py-10 cursor-pointer cc-nav-item" data-testid="analyzer-file-label">
            <Upload size={24} className="text-blue-300 mb-2" />
            <span className="text-sm text-slate-300">{file ? file.name : "Click to choose a PDF or CSV"}</span>
            <input type="file" accept=".pdf,.csv,.txt" className="hidden" onChange={(e) => setFile(e.target.files[0])} data-testid="analyzer-file-input" />
          </label>
        )}

        <button onClick={analyze} disabled={loading} data-testid="analyzer-run"
          className="cc-btn-primary inline-flex items-center gap-2 rounded-xl px-6 py-3 text-sm font-500 mt-4 disabled:opacity-60">
          <Sparkles size={16} /> {loading ? "Analyzing…" : "Analyze Report"}
        </button>
      </GlassCard>

      <GlassCard className="p-6">
        <h3 className="cc-heading text-base font-500 text-white mb-1">Import from a Credit Bureau</h3>
        <p className="text-sm text-slate-400 mb-4">Connect a bureau to import a report automatically. Demo mode uses sample data — a real bureau API can be connected later.</p>
        <div className="grid sm:grid-cols-3 gap-3">
          {bureaus.map((b) => (
            <div key={b.bureau} className="rounded-xl cc-surface border border-white/[0.05] p-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-white">{b.bureau}</span>
                {b.connected ? <Chip tone="green">Connected</Chip> : <Chip>Not connected</Chip>}
              </div>
              {b.connected ? (
                <button onClick={() => importBureau(b.bureau)} disabled={bureauBusy === b.bureau}
                  className="cc-btn-primary w-full rounded-lg py-2 text-xs font-500 mt-3 disabled:opacity-60" data-testid={`import-${b.bureau}`}>
                  {bureauBusy === b.bureau ? "Importing…" : "Import & Analyze"}
                </button>
              ) : (
                <button onClick={() => connectBureau(b.bureau)} disabled={bureauBusy === b.bureau}
                  className="w-full rounded-lg border border-white/12 py-2 text-xs text-white cc-nav-item mt-3" data-testid={`connect-${b.bureau}`}>
                  {bureauBusy === b.bureau ? "Connecting…" : "Connect"}
                </button>
              )}
            </div>
          ))}
        </div>
      </GlassCard>

      {loading && <Spinner />}

      {result && (
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="grid lg:grid-cols-3 gap-5">
          <GlassCard className="p-6 flex flex-col items-center justify-center text-center">
            <ScoreRing value={result.health} label="Health" size={140} />
            <p className="text-slate-400 text-sm mt-4">Educational credit-health estimate</p>
          </GlassCard>

          <GlassCard className="p-6 lg:col-span-2">
            <h3 className="cc-heading text-lg font-500 text-white mb-2">Plain-English Summary</h3>
            <p className="text-slate-300 text-sm leading-relaxed">{result.summary}</p>
            {result.funding_insight && (
              <div className="mt-4 rounded-xl cc-surface border border-white/[0.05] p-4">
                <div className="text-xs uppercase tracking-widest text-blue-300 mb-1">Funding Insight</div>
                <p className="text-sm text-slate-300">{result.funding_insight}</p>
              </div>
            )}
          </GlassCard>

          <GlassCard className="p-6 lg:col-span-2">
            <h3 className="cc-heading text-lg font-500 text-white mb-4 flex items-center gap-2">
              <AlertCircle size={17} className="text-amber-300" /> Findings
            </h3>
            <div className="space-y-2.5">
              {(result.findings || []).length === 0 && <p className="text-sm text-slate-500">No specific issues identified.</p>}
              {(result.findings || []).map((f, i) => (
                <div key={`${f.type}-${(f.detail || "").slice(0, 40)}-${i}`} className="rounded-xl cc-surface border border-white/[0.05] p-3.5">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-500 text-white">{f.type}</span>
                    <Chip tone={SEV[f.severity] || "default"}>{f.severity}</Chip>
                  </div>
                  <p className="text-sm text-slate-400 mt-1">{f.detail}</p>
                </div>
              ))}
            </div>
          </GlassCard>

          <GlassCard className="p-6">
            <h3 className="cc-heading text-lg font-500 text-white mb-4 flex items-center gap-2">
              <CheckCircle2 size={17} className="text-blue-300" /> Priority Action Plan
            </h3>
            <ol className="space-y-2.5">
              {(result.action_plan || []).map((a, i) => (
                <li key={`${a.slice(0, 40)}-${i}`} className="flex gap-2.5 text-sm text-slate-300">
                  <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full cc-gradient text-white text-[11px]">{i + 1}</span>
                  {a}
                </li>
              ))}
            </ol>
          </GlassCard>
        </motion.div>
      )}

      <Disclaimer text="This AI analysis is educational only and does not guarantee credit score changes, removals, or funding approval." />

      {history.length > 0 && (
        <GlassCard className="p-6">
          <h3 className="cc-heading text-base font-500 text-white mb-4 flex items-center gap-2"><FileText size={16} /> Analysis History</h3>
          <div className="space-y-2">
            {history.map((h) => (
              <button key={h.id} onClick={() => setResult(h)} className="w-full flex items-center justify-between rounded-lg cc-surface border border-white/[0.05] px-4 py-3 text-left cc-nav-item">
                <span className="text-sm text-slate-200">{h.bureau} report {h.filename ? `· ${h.filename}` : ""}</span>
                <span className="text-xs text-slate-500">{new Date(h.created_at).toLocaleDateString()}</span>
              </button>
            ))}
          </div>
        </GlassCard>
      )}
    </div>
  );
}
